import {
  CountryCode,
  LanguageCode,
  Notch,
  type CountryCodeStrict,
  type LanguageCodeStrict,
} from '../typings';
import { LANGUAGE } from '../constants';
import { COUNTRY_CODE } from '../constants';

/**
 * @private
 */
const DEFAULT_NOTCH_THOUSAND = 1_000;
/**
 * @private
 */
const DEFAULT_NOTCH_MILLION = 1_000_000;
/**
 * @private
 */
const DEFAULT_NOTCH_BILLION = 1_000_000_000;
/**
 * @private
 */
const DEFAULT_NOTCH_TEN_THOUSAND = 10_000;
/**
 * @private
 */
const DEFAULT_NOTCH_HUNDRED_THOUSAND = 100_000;
/**
 * @private
 */
const DEFAULT_NOTCH_TEN_MILLION = 10_000_000;
/**
 * @private
 */
const DEFAULT_NOTCH_HUNDRED_MILLION = 100_000_000;
/**
 * @private
 */
const DEFAULT_NOTCH_TRILLION = 1_000_000_000_000;

/**
 * Default compact number calculation configurations.
 * Use in {@link compactNumber}
 * 1. 1,000,000,000 => b
 * 2. 1,000,000 => m
 * 3. 1,000 => k
 * @const {Notch[]}
 */
export const DEFAULT_NOTCHES: Notch[] = [
  { notch: DEFAULT_NOTCH_BILLION, unit: 'b' },
  { notch: DEFAULT_NOTCH_MILLION, unit: 'm' },
  { notch: DEFAULT_NOTCH_THOUSAND, unit: 'k' },
];

/**
 * Structure is [countryCode][languageCode | 'default'] = Notches[]
 *
 * 'default' must refer to the default language code for the country
 */
const COUNTRY_AND_LANGUAGE_NOTCH_CONF: Record<
  CountryCodeStrict,
  Partial<Record<LanguageCodeStrict | 'default', Notch[] | undefined>>
> = {
  [COUNTRY_CODE.SG]: {
    default: DEFAULT_NOTCHES,
    [LANGUAGE['zh-Hans']]: [
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '亿' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '万' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: '千' },
    ],
    [LANGUAGE.ms]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.ID]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'M' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'JT' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'RB' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.MY]: {
    default: DEFAULT_NOTCHES,
    [LANGUAGE.ms]: DEFAULT_NOTCHES,
    [LANGUAGE['zh-Hans']]: [
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '亿' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '万' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: '千' },
    ],
  },
  [COUNTRY_CODE.PH]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'B' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'M' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'K' },
    ],
    [LANGUAGE['zh-Hans']]: [
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '亿' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '万' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: '千' },
    ],
  },
  [COUNTRY_CODE.TW]: {
    default: [
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '億' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '萬' },
    ],
    [LANGUAGE['zh-Hans']]: [
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '亿' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '万' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: '千' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.TH]: {
    default: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.VN]: {
    default: [
      { notch: DEFAULT_NOTCH_MILLION, unit: 'tr' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'k' },
    ],
    [LANGUAGE.km]: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'ពាន់លាន' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'លាន' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'ពាន់' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.IR]: {
    default: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.HK]: {
    default: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.CN]: {
    default: [{ notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '万' }],
  },
  [COUNTRY_CODE.BR]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'bi' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'mi' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'mil' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.MX]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'mil millones' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'millón' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'mil' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.CO]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'mil millones' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'millón' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'mil' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.CL]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'mil millones' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'millón' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'mil' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.AR]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'mm' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'm' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'k' },
    ],
  },
  [COUNTRY_CODE.PL]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: ' mld' },
      { notch: DEFAULT_NOTCH_MILLION, unit: ' mln' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: ' tys.' },
    ],
  },
  [COUNTRY_CODE.ES]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'MM' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'M' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'k' },
    ],
  },

  [COUNTRY_CODE.FR]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: ' Md' },
      { notch: DEFAULT_NOTCH_MILLION, unit: ' M' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: ' k' },
    ],
  },
  [COUNTRY_CODE.IN]: {
    default: [
      { notch: DEFAULT_NOTCH_TEN_MILLION, unit: ' करोड़' },
      { notch: DEFAULT_NOTCH_HUNDRED_THOUSAND, unit: ' लाख' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: ' हजार' },
    ],
    [LANGUAGE.en]: [
      { notch: DEFAULT_NOTCH_TEN_MILLION, unit: ' cr' },
      { notch: DEFAULT_NOTCH_HUNDRED_THOUSAND, unit: ' lakh' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'k' },
    ],
  },
  [COUNTRY_CODE.US]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'B' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'M' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'k' },
    ],
  },
  [COUNTRY_CODE.KR]: {
    default: [
      { notch: DEFAULT_NOTCH_TRILLION, unit: '조' },
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '억' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '만' },
    ],
  },
  [COUNTRY_CODE.JP]: {
    default: [
      { notch: DEFAULT_NOTCH_TRILLION, unit: '兆' },
      { notch: DEFAULT_NOTCH_HUNDRED_MILLION, unit: '億' },
      { notch: DEFAULT_NOTCH_TEN_THOUSAND, unit: '万' },
    ],
  },
  [COUNTRY_CODE.KH]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: ' ពាន់លាន' },
      { notch: DEFAULT_NOTCH_MILLION, unit: ' លាន' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: ' ពាន់' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.MM]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'B' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'M' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'K' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
  [COUNTRY_CODE.LA]: {
    default: [
      { notch: DEFAULT_NOTCH_BILLION, unit: 'B' },
      { notch: DEFAULT_NOTCH_MILLION, unit: 'M' },
      { notch: DEFAULT_NOTCH_THOUSAND, unit: 'K' },
    ],
    [LANGUAGE.en]: DEFAULT_NOTCHES,
  },
} as const;

export function getNotchesByCountryAndLanguage(
  country?: CountryCode,
  language?: LanguageCode
): Notch[] {
  if (!country) {
    return DEFAULT_NOTCHES;
  }
  const countryConfig =
    COUNTRY_AND_LANGUAGE_NOTCH_CONF[country as CountryCodeStrict];
  const countryDefaultNotches = countryConfig.default as Notch[];

  if (!language || !(language in countryConfig)) {
    return countryDefaultNotches;
  }

  return (
    (countryConfig[language as LanguageCodeStrict] as Notch[]) ||
    countryDefaultNotches
  );
}
